import { useEffect, useState } from 'react';
import { Badge, Button } from '@/components/ui/primitives';
import { Drawer } from '@/components/ui/Drawer';
import { cn } from '@/lib/utils';
import type { LibraryBlogItem, LibraryItem, LibrarySocialItem } from '@/data/sources-library';
import { channelName, formatDayTime, sourceLabel, targetLabel, wpLabel } from './libraryModel';
import { ItemStatus } from './LibraryRows';

/* Preview drawer (design §4.1 item 5): Blog / per-channel tabs, the decision
 * metadata, and ONLY the existing per-item actions. Every action calls the
 * existing endpoint and shows the server's real result; nothing publishes here. */

export interface DrawerActions {
  open: (item: LibraryItem) => void;
  remove: (item: LibraryItem) => void;
  approve: (item: LibrarySocialItem) => void;
  requestChanges: (item: LibrarySocialItem) => void;
  submit: (item: LibrarySocialItem) => void;
}

function Kv({ rows }: { rows: Array<[string, React.ReactNode]> }) {
  return (
    <dl className="grid grid-cols-[110px_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-xs">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-text-secondary">{k}</dt>
          <dd className="min-w-0 break-words text-text-primary">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function LibraryPreviewDrawer({ item, siteLabel, onClose, actions, busy, message }: {
  item: LibraryItem | null; siteLabel: string; onClose: () => void; actions: DrawerActions; busy: boolean; message: string | null;
}) {
  const [tab, setTab] = useState<number>(0); // 0 = the item itself; n = nth variant (blog only)
  useEffect(() => { setTab(0); }, [item?.kind, item?.id]);
  if (!item) return null;
  const variants = item.kind === 'blog' ? item.children : [];
  const shown: LibraryItem = tab > 0 && variants[tab - 1] ? variants[tab - 1] : item;
  const site = item.domain ? item.domain.label : siteLabel;

  const meta: Array<[string, React.ReactNode]> = [
    ['Site', site || '—'],
    ['Target page', item.target_page ? <span title={item.target_page}>{targetLabel(item.target_page)}</span> : (item.kind === 'blog' ? 'Best-matched page (not recorded)' : '—')],
    ['Keyword', item.keyword || '—'],
    ['Source', sourceLabel(item.source)],
    ['Created', formatDayTime(item.created_at) || '—'],
    ['Updated', formatDayTime(item.updated_at) || '—'],
  ];
  if (item.kind === 'blog') {
    const w = wpLabel(item.wp.state);
    meta.push(['WordPress', (
      <span className="flex flex-wrap items-center gap-1.5">
        <Badge tone={w.tone}>{w.label}</Badge>
        {item.wp.edit_url && <a className="text-ai underline" href={item.wp.edit_url} target="_blank" rel="noopener noreferrer">Open in WordPress</a>}
      </span>
    )]);
    if (item.wp.state === 'failed' && item.wp.error) meta.push(['Last push', <span className="text-danger">{item.wp.error}</span>]);
    if (item.wp.scheduled_for) meta.push(['Scheduled', formatDayTime(item.wp.scheduled_for)]);
  } else if (item.scheduled_at) {
    meta.push(['Scheduled', formatDayTime(item.scheduled_at) + ' (local schedule)']);
  }
  if (item.kind === 'social' && item.parent) meta.push(['From blog', item.parent.title]);

  return (
    <Drawer open onClose={onClose} title={<span className="flex items-center gap-2"><ItemStatus item={item} /> Preview</span>} widthClass="w-full max-w-[460px]">
      <h3 className="font-display text-lg font-semibold leading-snug text-text-primary">{item.title || 'Untitled'}</h3>
      {item.kind === 'blog' && <p className="mt-0.5 text-xs text-text-secondary">/{item.slug || 'no-slug'}</p>}
      <div className="mt-3"><Kv rows={meta} /></div>

      {variants.length > 0 && (
        <div role="tablist" aria-label="Blog and social versions" className="mt-4 flex gap-1 overflow-x-auto border-b border-border-subtle">
          {[{ label: 'Blog', i: 0 }, ...variants.map((v, i) => ({ label: channelName(v.channel), i: i + 1 }))].map((t) => (
            <button key={t.i} role="tab" aria-selected={tab === t.i} onClick={() => setTab(t.i)}
              className={cn('min-h-[40px] shrink-0 border-b-2 px-3 text-xs', tab === t.i ? 'border-ai font-semibold text-ai' : 'border-transparent text-text-secondary')}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      <div className="mt-3 rounded-md border border-border-subtle p-3 text-sm text-text-secondary">
        {shown.kind === 'social' ? (
          <>
            <div className="mb-1 flex items-center justify-between gap-2 text-xs text-text-secondary">
              <span>{channelName(shown.channel)} · {shown.format === 'carousel' ? `Carousel · ${shown.pages} pages` : 'Single'}</span>
              <ItemStatus item={shown} />
            </div>
            <p className="whitespace-pre-line">{shown.excerpt || 'No copy yet.'}</p>
          </>
        ) : (
          <p className="text-xs">Open the blog to read, edit, copy, repurpose for social or push to WordPress with its confirm step.</p>
        )}
      </div>

      {message && <p role="status" className="mt-3 text-sm text-text-primary">{message}</p>}

      <ItemActions item={shown} actions={actions} busy={busy} />
      <p className="mt-3 text-[11px] text-text-secondary">Each action uses the existing step and shows the server’s result. Nothing is published from this panel.</p>
    </Drawer>
  );
}

function ItemActions({ item, actions, busy }: { item: LibraryItem; actions: DrawerActions; busy: boolean }) {
  if (item.kind === 'blog') {
    const b = item as LibraryBlogItem;
    const canOpen = b.status === 'ready';
    return (
      <div className="mt-4 flex flex-wrap gap-2">
        <Button variant="primary" disabled={!canOpen || busy} onClick={() => actions.open(b)} title={canOpen ? undefined : 'Only ready drafts open in the editor'}>Open blog</Button>
        <Button variant="secondary" disabled={busy} onClick={() => actions.remove(b)} className="text-danger">Delete</Button>
        {!canOpen && <p className="w-full text-[11px] text-text-secondary">{b.status === 'generating' ? 'This draft is still generating.' : 'This draft did not finish generating. Ask Aura to write it again.'}</p>}
      </div>
    );
  }
  const s = item as LibrarySocialItem;
  return (
    <div className="mt-4 flex flex-wrap gap-2">
      <Button variant="primary" disabled={busy} onClick={() => actions.open(s)}>Open in editor</Button>
      {(s.status === 'draft' || s.status === 'rejected') && <Button variant="secondary" disabled={busy} onClick={() => actions.submit(s)}>Submit for review</Button>}
      {s.status === 'in_review' && <Button variant="secondary" disabled={busy} onClick={() => actions.approve(s)}>Approve</Button>}
      {s.status === 'in_review' && <Button variant="secondary" disabled={busy} onClick={() => actions.requestChanges(s)}>Request changes…</Button>}
      <Button variant="secondary" disabled={busy} onClick={() => actions.remove(s)} className="text-danger">Delete</Button>
    </div>
  );
}
